import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type {
  SearchFilterItem,
  SearchRequestFilters,
  SearchResponseFilters,
} from '@/types/search';
import { SearchResultSortingRule } from '@/types/search';
import isEnumValue from '@/util/isEnumValue';
import getCurrentLangCode from '@/util/getCurrentLangCode';
import { maxAllowedTotalPages } from '@/components/pages/SearchPage/constants';

export const pageUrlParam = 'pagenumber';

const decimalIntegerPattern = /^\d+$/;

export type FilterKey = keyof SearchRequestFilters;

export type MultiFilterKey = Exclude<FilterKey, 'sector'>;
export type SingleFilterKey = Extract<FilterKey, 'sector'>;

export const multiSelectFilterFields: MultiFilterKey[] = [
  'pageType',
  'productType',
  'languages',
  'statuses',
  'publishers',
] as const;

export const singleSelectFilterFields: SingleFilterKey[] = ['sector'] as const;

export const urlParamToFilterKey: Partial<Record<string, FilterKey>> = {
  productTypeFilter: 'productType',
  SdoFilter: 'publishers',
  SectorFilter: 'sector',
  LanguageFilter: 'languages',
} as const;

export const filterKeyToUrlParam: Partial<Record<FilterKey, string>> = {
  productType: 'productTypeFilter',
  publishers: 'SdoFilter',
  sector: 'SectorFilter',
  languages: 'LanguageFilter',
} as const;

export interface FilterItem {
  key: FilterKey;
  value: string;
}

export interface UrlSearchState {
  q: string;
  activeSort: SearchResultSortingRule;
  filters: FilterItem[];
  page: number;
}

export interface SearchState {
  query: string;
  page: number;
  perPage: number;
  activeSort: SearchResultSortingRule;
  filters: FilterItem[];
  activeLanguage: string;
}

export const defaultState: SearchState = {
  query: '',
  page: 0,
  perPage: 6,
  activeSort: SearchResultSortingRule.Relevance,
  filters: [],
  activeLanguage: '',
};

export const parseActiveSort = (
  params: URLSearchParams,
): SearchResultSortingRule => {
  const activeSortFromUrl = params.get('SortRule');

  if (isEnumValue(SearchResultSortingRule, activeSortFromUrl)) {
    return activeSortFromUrl;
  }

  return SearchResultSortingRule.Relevance;
};

export const parsePage = (params: URLSearchParams): number => {
  const raw = params.get(pageUrlParam);

  if (raw === null) {
    return defaultState.page;
  }

  const isDecimalInteger = decimalIntegerPattern.test(raw.trim());

  if (!isDecimalInteger) {
    return defaultState.page;
  }

  const parsed = Number(raw);

  if (!Number.isInteger(parsed) || parsed < 1 || parsed > maxAllowedTotalPages) {
    return defaultState.page;
  }

  return parsed - 1;
};

export const isMultiFilterKey = (key: string): key is MultiFilterKey =>
  (multiSelectFilterFields as string[]).includes(key);

export const isSingleFilterKey = (key: string): key is SingleFilterKey =>
  (singleSelectFilterFields as string[]).includes(key);

const parseFilterValues = (raw: string): string[] => {
  if (!raw.startsWith("'") || !raw.endsWith("'")) {
    return [];
  }
  const parts = raw.split("','");
  parts[0] = parts[0].slice(1);
  parts[parts.length - 1] = parts[parts.length - 1].slice(0, -1);
  return parts.filter(Boolean);
};

const parseSingleFilterValue = (raw: string): string | undefined => {
  if (!raw.startsWith("'") || !raw.endsWith("'")) {
    return undefined;
  }
  return raw.slice(1, -1) || undefined;
};

type MultiValueId = `${MultiFilterKey}::${string}`;
export const parseFilters = (params: URLSearchParams): FilterItem[] => {
  const result: FilterItem[] = [];
  const seenMultiValues = new Set<MultiValueId>();
  const seenSingleKeys = new Set<SingleFilterKey>();

  params.forEach((rawValue, urlKey) => {
    if (!rawValue) {
      return;
    }

    const mappedKey = urlParamToFilterKey[urlKey];
    const key = (mappedKey ?? urlKey) as FilterKey;

    if (isMultiFilterKey(key)) {
      const values =
        mappedKey === undefined ? [rawValue] : parseFilterValues(rawValue);

      values.filter(Boolean).forEach((value) => {
        const id: MultiValueId = `${key}::${value}`;
        if (seenMultiValues.has(id)) {
          return;
        }

        seenMultiValues.add(id);
        result.push({
          key,
          value,
        });
      });

      return;
    }

    if (isSingleFilterKey(key)) {
      if (seenSingleKeys.has(key)) {
        return;
      }

      const value =
        mappedKey === undefined ? rawValue : parseSingleFilterValue(rawValue);

      if (!value) {
        return;
      }

      seenSingleKeys.add(key);
      result.push({
        key,
        value,
      });
    }
  });

  return result;
};

const toggleFilterItem = (
  current: FilterItem[],
  key: FilterKey,
  value: string,
  checked: boolean,
): FilterItem[] => {
  if (!checked) {
    return current.filter((item) => !(item.key === key && item.value === value));
  }

  if (isSingleFilterKey(key)) {
    const withoutKey = current.filter((item) => item.key !== key);

    return [
      ...withoutKey,
      {
        key,
        value,
      },
    ];
  }

  const exists = current.some((item) => item.key === key && item.value === value);

  if (exists) {
    return current;
  }

  return [
    ...current,
    {
      key,
      value,
    },
  ];
};

const mergeMultiFromServer = (
  current: FilterItem[],
  key: MultiFilterKey,
  nextItems?: SearchFilterItem[],
): FilterItem[] => {
  if (!nextItems) {
    return current;
  }

  const selectedNext = nextItems.filter((item) => item.selected);

  if (selectedNext.length === 0) {
    return current.filter((item) => item.key !== key);
  }

  const nextValuesSet = new Set(selectedNext.map((item) => item.value));

  const keptItems: FilterItem[] = [];
  const existingValuesForKey: string[] = [];

  current.forEach((item) => {
    if (item.key !== key) {
      keptItems.push(item);
      return;
    }

    if (nextValuesSet.has(item.value)) {
      keptItems.push(item);
      existingValuesForKey.push(item.value);
    }
  });

  const existingSet = new Set(existingValuesForKey);

  const newEntries: FilterItem[] = selectedNext
    .filter((item) => !existingSet.has(item.value))
    .map((item) => ({
      key,
      value: item.value,
    }));

  return [...keptItems, ...newEntries];
};

const mergeSingleFromServer = (
  current: FilterItem[],
  key: SingleFilterKey,
  nextItems?: SearchFilterItem[],
): FilterItem[] => {
  const selectedItem = nextItems?.find((item) => item.selected);

  if (!selectedItem) {
    return current.filter((item) => item.key !== key);
  }

  let hasInserted = false;
  let hasSeenSingleKeyFilter = false;

  const result: FilterItem[] = [];

  current.forEach((item) => {
    if (item.key !== key) {
      result.push(item);
      return;
    }

    if (!hasSeenSingleKeyFilter) {
      result.push({
        key,
        value: selectedItem.value,
      });

      hasSeenSingleKeyFilter = true;
      hasInserted = true;
    }
  });

  if (!hasInserted) {
    result.push({
      key,
      value: selectedItem.value,
    });
  }

  return result;
};

export const buildSearchStateFromUrl = (): SearchState => {
  const params = new URLSearchParams(globalThis.location.search);
  const query = params.get('q') ?? defaultState.query;
  const activeLanguage = getCurrentLangCode(globalThis.location.pathname);

  return {
    ...defaultState,
    query,
    activeSort: parseActiveSort(params),
    filters: parseFilters(params),
    page: parsePage(params),
    activeLanguage,
  };
};

const initialState: SearchState = buildSearchStateFromUrl();

export const searchSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    setQuery: (state, action: PayloadAction<string>) => {
      state.query = action.payload;
      state.page = defaultState.page;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setPageFromPagination: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    correctOutOfRangePage: (state) => {
      state.page = defaultState.page;
    },
    setActiveSort: (state, action: PayloadAction<SearchResultSortingRule>) => {
      state.activeSort = action.payload;
    },
    removeFilters: (state) => {
      state.page = defaultState.page;
      state.filters = [];
    },
    updateFilter: (
      state,
      action: PayloadAction<{
        filter: FilterKey;
        value: string;
        checked: boolean;
      }>,
    ) => {
      const { filter, value, checked } = action.payload;

      state.filters = toggleFilterItem(state.filters, filter, value, checked);
      state.page = defaultState.page;
    },

    updateFiltersFromServer: (
      state,
      action: PayloadAction<{
        filters: SearchResponseFilters;
      }>,
    ) => {
      const { filters } = action.payload;

      let nextFilters = state.filters;

      nextFilters = mergeMultiFromServer(nextFilters, 'pageType', filters.pageType);
      nextFilters = mergeMultiFromServer(
        nextFilters,
        'productType',
        filters.productType,
      );
      nextFilters = mergeSingleFromServer(nextFilters, 'sector', filters.sector);
      nextFilters = mergeMultiFromServer(nextFilters, 'statuses', filters.statuses);
      nextFilters = mergeMultiFromServer(
        nextFilters,
        'languages',
        filters.languages,
      );
      nextFilters = mergeMultiFromServer(
        nextFilters,
        'publishers',
        filters.publishers,
      );

      state.filters = nextFilters;
    },

    replaceFromUrl(state, action: PayloadAction<UrlSearchState>) {
      const { q, activeSort, filters, page } = action.payload;

      state.query = q;
      state.activeSort = activeSort;
      state.filters = filters;
      state.page = page;
    },
  },
});

export const {
  setPage,
  setPageFromPagination,
  correctOutOfRangePage,
  setActiveSort,
  removeFilters,
  updateFilter,
  updateFiltersFromServer,
  replaceFromUrl,
  setQuery,
} = searchSlice.actions;

export default searchSlice.reducer;
